import { useState, type ReactElement } from 'react'
import { Send, Check } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Badge } from '../components/ui/badge.js'
import { useStep } from '../state/useStep.js'
import { useDemoStore, type QAAnswers } from '../state/store.js'

type Q = {
  key: keyof QAAnswers
  prompt: string
  chips: { label: string; value: string }[]
  allowFreeform?: boolean
  placeholder?: string
}

const QUESTIONS: Q[] = [
  {
    key: 'level',
    prompt: 'Is your current dishwasher sitting level, or is it shimmed or tilted?',
    chips: [
      { label: "It's level", value: 'level' },
      { label: "It's shimmed", value: 'shimmed' },
      { label: "It's not level", value: 'not-level' },
      { label: "I'm not sure", value: 'not-sure' },
    ],
  },
  {
    key: 'waterPressure',
    prompt: 'Have you noticed low water pressure in your kitchen?',
    chips: [
      { label: 'No, seems normal', value: 'normal' },
      { label: "Yes, it's low", value: 'low' },
      { label: 'Not sure', value: 'not-sure' },
    ],
  },
  {
    key: 'knownIssues',
    prompt: 'Any problems with your current dishwasher install we should know about?',
    chips: [
      { label: 'No known issues', value: 'No known issues' },
      { label: "I don't know", value: "I don't know" },
    ],
    allowFreeform: true,
    placeholder: 'e.g., leaks, tight access, door clearance…',
  },
]

const LABEL: Record<string, string> = {
  level: 'Level', waterPressure: 'Water Pressure', knownIssues: 'Known Issues',
}

export const QAScreen = (): ReactElement => {
  const { goto } = useStep()
  const { dispatch } = useDemoStore()
  const [answers, setAnswers] = useState<Partial<QAAnswers>>({})
  const [freeform, setFreeform] = useState('')

  const idx = Object.keys(answers).length
  const current = QUESTIONS[idx]

  const answer = (value: string): void => {
    if (!current) return
    const next = { ...answers, [current.key]: value } as Partial<QAAnswers>
    setAnswers(next)
    if (Object.keys(next).length === QUESTIONS.length) {
      dispatch({ type: 'SET_QA', value: next as QAAnswers })
      goto('review')
    }
  }

  const footer = current && (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {current.chips.map(chip => (
          <button
            key={chip.value}
            type="button"
            onClick={() => answer(chip.value)}
            className="rounded-full bg-muted px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors min-h-[36px]"
          >
            {chip.label}
          </button>
        ))}
      </div>
      {current.allowFreeform && (
        <div className="flex items-center gap-2 border border-input rounded-full px-4 py-2 bg-background">
          <input
            value={freeform}
            onChange={e => setFreeform(e.target.value)}
            placeholder={current.placeholder}
            className="flex-1 outline-none text-sm bg-transparent"
          />
          <button
            type="button"
            onClick={() => freeform.trim() && answer(freeform.trim())}
            className="text-primary p-1 rounded-full hover:bg-primary/10"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )

  return (
    <AppShell title="Guided questions" showBack onBack={() => goto('undersink-results')} progress={0.75} footer={footer}>
      <div className="text-center text-xs uppercase tracking-wider text-muted-foreground mb-4">
        Question {Math.min(idx + 1, QUESTIONS.length)} of {QUESTIONS.length}
      </div>

      {/* History — all already-answered questions render their chat bubbles */}
      {QUESTIONS.slice(0, idx).map(q => (
        <div key={q.key} className="mb-4">
          <div className="bg-muted text-foreground rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
            {q.prompt}
          </div>
          <div className="flex justify-end mt-2">
            <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-4 py-3 max-w-[85%]">
              {(answers as Record<string, string>)[q.key]}
            </div>
          </div>
          <div className="mt-2">
            <Badge variant="success">
              <Check className="h-3 w-3" /> {LABEL[q.key]}: {(answers as Record<string, string>)[q.key]}
            </Badge>
          </div>
        </div>
      ))}

      {/* Current question */}
      {current && (
        <div className="mb-6">
          <div className="bg-muted text-foreground rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
            {current.prompt}
          </div>
        </div>
      )}
    </AppShell>
  )
}
